import { Stack } from '@fluentui/react';
import { ProgressBar, Spinner, SpinnerProps, Text, Tooltip, Tree, TreeItem, TreeItemLayout } from '@fluentui/react-components';
import {
	CheckmarkCircleRegular,
	CheckmarkCircleWarningRegular,
	CircleHintHalfVerticalRegular,
	CircleHintRegular,
	ErrorCircleRegular,
} from '@fluentui/react-icons';
import React, { FC } from 'react';

import CopyLink from '../copy-link/CopyLink';
import useStyle from './StepProgressStyle';

export enum StepStatus {
	Submitted = 'SUBMITTED',
	Starting = 'STARTING',
	Running = 'RUNNING',
	CancelRequested = 'CANCEL_REQUESTED',
	PauseRequested = 'PAUSE_REQUESTED',
	Paused = 'PAUSED',
	Failed = 'FAILED',
	Cancelled = 'CANCELLED',
	Skipped = 'SKIPPED',
	Completed = 'COMPLETED',
	PartialCompleted = 'PARTIAL_COMPLETED',
	Active = 'ACTIVE',
}
export type Steps = {
	id: string;
	message: string;
	state: StepStatus;
	startTime?: string;
	childrens?: Steps[];
};
export type StepProgressPropType = {
	title: string;
	message: string | React.JSX.Element;
	icon: React.JSX.Element;
	url?: string;
	urlText?: string;
	steps?: Steps[];
	spinnerProps?: Omit<SpinnerProps, 'label'>;
};

const StepProgress: FC<StepProgressPropType> = ({ message, title, icon, steps, spinnerProps, url, urlText }: StepProgressPropType) => {
	const styles = useStyle();
	const getIcon = (status: StepStatus) => {
		switch (status) {
			case StepStatus.Completed:
				return <CheckmarkCircleRegular className={styles.iconColorSize} />;
			case StepStatus.PartialCompleted:
				return <CheckmarkCircleWarningRegular className={styles.iconColorSize} />;
			case StepStatus.Failed:
				return <ErrorCircleRegular className={styles.iconColorSize} />;
			case StepStatus.Submitted:
				return <CircleHintRegular className={styles.iconColorSize} />;
			case StepStatus.Running:
				return <Spinner {...spinnerProps} />;
			default:
				return <CircleHintHalfVerticalRegular className={styles.iconColorSize} />;
		}
	};
	const renderTreeItems = (steps: Steps[]) =>
		steps?.map((el) => {
			if (el.childrens && el.childrens?.length > 0) {
				return (
					<TreeItem itemType="branch" value={el.id}>
						<TreeItemLayout iconBefore={getIcon(el.state)}>{el.message}</TreeItemLayout>
						<Tree>{renderTreeItems(el.childrens)}</Tree>
					</TreeItem>
				);
			}
			return (
				<TreeItem itemType="leaf" value={el.id}>
					<Tooltip content={el.message} relationship="label">
						<TreeItemLayout iconBefore={getIcon(el.state)}>{el.message}</TreeItemLayout>
					</Tooltip>
				</TreeItem>
			);
		});

	return (
		<Stack className={'stackPanel'} tokens={{ childrenGap: 10 }}>
			<Stack.Item>{icon}</Stack.Item>
			<Stack.Item>
				<Text size={400} weight={'medium'}>
					{title}
				</Text>
			</Stack.Item>
			<Stack.Item>
				<Text size={200} weight="regular">
					{message}
				</Text>
			</Stack.Item>
			<Stack.Item>
				<ProgressBar className={styles.progressBar} />
			</Stack.Item>
			{steps && steps.length > 0 ? (
				<Stack.Item>
					<Tree aria-label="Default">{renderTreeItems(steps)}</Tree>
				</Stack.Item>
			) : null}
			<Stack.Item>{url ? <CopyLink url={url} urlText={urlText} /> : null}</Stack.Item>
		</Stack>
	);
};

export default StepProgress;
